import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  SiOpenai,
  SiLangchain,
  SiMeta,
  SiHuggingface,
  SiEspressif,
  SiMqtt,
  SiGooglecloud,
  SiDocker,
  SiKubernetes,
  SiGitlab,
  SiTerraform,
} from "react-icons/si";
import { FaAws, FaMicrosoft } from "react-icons/fa";
import { Cpu, Cloud, Radio, Wifi, Server } from "lucide-react";

const groups = [
  {
    title: "AI",
    icon: Cpu,
    items: [
      { Icon: SiOpenai, label: "OpenAI" },
      { Icon: SiLangchain, label: "LangChain" },
      { Icon: SiMeta, label: "Llama" },
      { Icon: SiHuggingface, label: "Hugging Face" },
    ],
  },
  {
    title: "IoT",
    icon: Radio,
    items: [
      { Icon: SiEspressif, label: "ESP32" },
      { Icon: SiMqtt, label: "MQTT" },
      { Icon: Server, label: "ThingsBoard" },
      { Icon: Wifi, label: "LoRaWAN" },
    ],
  },
  {
    title: "Cloud",
    icon: Cloud,
    items: [
      { Icon: FaAws, label: "AWS" },
      { Icon: FaMicrosoft, label: "Azure" },
      { Icon: SiGooglecloud, label: "Google Cloud" },
    ],
  },
  {
    title: "DevOps",
    icon: SiKubernetes,
    items: [
      { Icon: SiDocker, label: "Docker" },
      { Icon: SiKubernetes, label: "Kubernetes" },
      { Icon: SiGitlab, label: "GitLab" },
      { Icon: SiTerraform, label: "Terraform" },
    ],
  },
];

const allLogos = groups.flatMap((g) => g.items);

export default function TechStack() {
  return (
    <section data-testid="tech-stack-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-3">
            Technology Stack
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Built on the tools the
            <span className="gradient-text-vivid"> world's best engineers</span> use
          </h2>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {groups.map((g, i) => {
            const GroupIcon = g.icon;
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#131C2F] border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 grid place-items-center">
                    <GroupIcon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">{g.title}</h3>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {g.items.map(({ Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/[0.02] border border-white/5 text-slate-300 hover:border-cyan-500/30 hover:text-white transition"
                    >
                      <Icon className="w-5 h-5 text-cyan-300" />
                      <span className="text-sm truncate">{label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 marquee-fade">
          <Marquee gradient={false} speed={30} pauseOnHover>
            {[...allLogos, ...allLogos].map(({ Icon, label }, i) => (
              <div key={i} className="mx-8 flex items-center gap-2 text-slate-400 hover:text-white transition">
                <Icon className="w-6 h-6" />
                <span className="font-heading text-sm">{label}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
}
